/**
 * RealtimeToast - Enhanced real-time notification toasts
 * Shows slide-in notifications with pulse effects for WebSocket updates
 */
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Bell, Users, DollarSign, Trophy, Clock } from 'lucide-react';

// Toast queue management
let toastQueue = [];
let addToastCallback = null;

// External function to add toasts from anywhere
export const showRealtimeToast = (toast) => {
  if (addToastCallback) {
    addToastCallback(toast);
  } else {
    toastQueue.push(toast);
  }
};

// Toast types with their configurations
const toastConfigs = {
  household_contribution: {
    icon: DollarSign,
    bgGradient: 'from-emerald-500 to-emerald-600',
    iconBg: 'bg-emerald-400',
    sound: 'coin'
  },
  household_bill: {
    icon: Users,
    bgGradient: 'from-purple-500 to-indigo-600',
    iconBg: 'bg-purple-400',
    sound: 'pop'
  },
  envelope_saved: {
    icon: Trophy,
    bgGradient: 'from-amber-500 to-orange-500',
    iconBg: 'bg-amber-400',
    sound: 'success'
  },
  envelope_reminder: {
    icon: Clock,
    bgGradient: 'from-orange-500 to-red-500',
    iconBg: 'bg-orange-400',
    sound: 'reminder'
  },
  invitation: {
    icon: Bell,
    bgGradient: 'from-blue-500 to-indigo-600',
    iconBg: 'bg-blue-400',
    sound: 'notification'
  },
  default: {
    icon: Bell,
    bgGradient: 'from-gray-700 to-gray-800',
    iconBg: 'bg-gray-500',
    sound: null
  }
};

// Individual toast component
function ToastItem({ toast, onDismiss }) {
  const [isExiting, setIsExiting] = useState(false);
  const config = toastConfigs[toast.type] || toastConfigs.default;
  const Icon = config.icon;
  
  useEffect(() => {
    // Auto dismiss after duration
    const timer = setTimeout(() => {
      setIsExiting(true);
      setTimeout(() => onDismiss(toast.id), 300);
    }, toast.duration || 5000);
    
    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, onDismiss]);
  
  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => onDismiss(toast.id), 300);
  };
  
  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 100, scale: 0.9 }}
      animate={{ 
        opacity: isExiting ? 0 : 1, 
        x: isExiting ? 100 : 0, 
        scale: isExiting ? 0.9 : 1 
      }}
      exit={{ opacity: 0, x: 100, scale: 0.9 }}
      transition={{ type: 'spring', damping: 20, stiffness: 300 }}
      className={`relative overflow-hidden rounded-2xl shadow-2xl mb-3 max-w-sm w-full bg-gradient-to-r ${config.bgGradient}`}
    >
      {/* Pulse overlay animation */}
      <motion.div
        className="absolute inset-0 bg-white/20"
        initial={{ scale: 0, opacity: 1 }}
        animate={{ scale: 2, opacity: 0 }}
        transition={{ duration: 0.6 }}
      />
      
      <div className="relative p-4 flex items-start gap-3">
        {/* Icon with pulse */}
        <motion.div
          className={`w-10 h-10 rounded-full ${config.iconBg} flex items-center justify-center flex-shrink-0`}
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 0.5, repeat: 2 }}
        >
          <Icon className="w-5 h-5 text-white" />
        </motion.div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="font-bold text-white text-sm">
            {toast.title}
          </p>
          <p className="text-white/80 text-sm mt-0.5 line-clamp-2">
            {toast.message}
          </p>
          {toast.subtitle && (
            <p className="text-white/60 text-xs mt-1">
              {toast.subtitle}
            </p>
          )}
        </div>
        
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="text-white/60 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      
      {/* Progress bar */}
      <motion.div
        className="h-1 bg-white/30"
        initial={{ width: '100%' }}
        animate={{ width: '0%' }}
        transition={{ duration: (toast.duration || 5000) / 1000, ease: 'linear' }}
      />
    </motion.div>
  );
}

// Toast container component
export function RealtimeToastContainer() {
  const [toasts, setToasts] = useState([]);
  
  const addToast = useCallback((toast) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { ...toast, id }]);
  }, []);
  
  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);
  
  // Register callback and process queue
  useEffect(() => {
    addToastCallback = addToast;
    
    // Process any queued toasts
    if (toastQueue.length > 0) {
      toastQueue.forEach(toast => addToast(toast));
      toastQueue = [];
    }
    
    return () => {
      addToastCallback = null;
    };
  }, [addToast]);
  
  return (
    <div className="fixed top-20 right-4 z-50 flex flex-col items-end">
      <AnimatePresence mode="popLayout">
        {toasts.map(toast => (
          <ToastItem
            key={toast.id}
            toast={toast}
            onDismiss={removeToast}
          />
        ))}
      </AnimatePresence>
    </div>
  );
}

export default RealtimeToastContainer;
